import { Button, Card, List, Modal, Spin, Tag, Typography } from '@/ui/compat/antd';
import {
  CheckCircleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LockOutlined,
  UserOutlined,
} from '@/ui/compat/icons';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import useUserPermissionsDetailed from './hooks/useUserPermissionsDetailed';

import moment from 'moment'
const { Title, Paragraph, Text } = Typography;

const NoPermissionsView = ({ title, description, permissions = [] }) => {
  const { t } = useTranslation();

  // Use translations for defaults if not provided
  const displayTitle = title || t('noPermissionsView.title', 'Interface Administrateur');
  const displayDescription =
    description || t('noPermissionsView.description', "Accès à l'interface d'administration");
  const displayPermissions =
    permissions.length > 0
      ? permissions
      : [
          {
            code: 'admin.access',
            name: t('noPermissionsView.defaultPermissions.admin.name', 'Accès administrateur'),
            description: t(
              'noPermissionsView.defaultPermissions.admin.description',
              "Accès général à l'interface d'administration"
            ),
          },
          {
            code: 'dashboard.view',
            name: t(
              'noPermissionsView.defaultPermissions.dashboard.name',
              'Voir le tableau de bord'
            ),
            description: t(
              'noPermissionsView.defaultPermissions.dashboard.description',
              'Accès au tableau de bord principal'
            ),
          },
        ];
  const navigate = useNavigate();
  const location = useLocation();
  // const { hasNoPermissions } = useUserPermissions();

  // Debug logging
  console.log('NoPermissionsView props:', { title, description, permissions });

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // State for permissions modal
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);

  // Use the detailed permissions hook
  const { userPermissions, permissionsLoading, error, refetch } = useUserPermissionsDetailed();

  const requiredPermissions = {
    title: displayTitle,
    description: displayDescription,
    permissions: displayPermissions,
  };

  const handleGoToProfile = () => {
    // All users go to admin profile (unified interface)
    navigate('/profile');
  };

  const handleRequestPermissions = () => {
    // All users go to admin requests (unified interface)
    navigate('/demandes-acces');
  };

  const handleShowPermissions = async () => {
    setPermissionsModalVisible(true);
    // Refetch permissions when modal opens
    await refetch();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px',
          alignItems: 'start',
        }}
      >
        {/* Left Side - Main Message */}
        <Card
          style={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid var(--gray-300)',
            background: 'white',
          }}
        >
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'var(--error-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <LockOutlined style={{ fontSize: '32px', color: 'white' }} />
            </div>

            <Title
              level={1}
              style={{
                color: 'var(--text-heading)',
                marginBottom: '16px',
                fontWeight: '600',
                fontSize: '28px',
              }}
            >
              {displayTitle}
            </Title>

            <Paragraph
              style={{
                fontSize: 'var(--font-size-md)',
                color: 'var(--text-muted)',
                marginBottom: '32px',
                lineHeight: '1.6',
              }}
            >
              {t('noPermissionsView.greeting', 'Bonjour')}{' '}
              <Text strong style={{ color: 'var(--text-heading)' }}>
                {user.name || user.email}
              </Text>
              ,{' '}
              {t(
                'noPermissionsView.noAccessMessage',
                "vous n'avez pas les permissions nécessaires pour accéder à cette interface."
              )}
            </Paragraph>

            <Paragraph
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--text-muted)',
                marginBottom: '24px',
                lineHeight: '1.5',
                fontStyle: 'italic',
              }}
            >
              {displayDescription}
            </Paragraph>

            <div
              style={{
                background: 'var(--gray-100)',
                padding: '20px',
                borderRadius: 'var(--radius-md)',
                marginBottom: '32px',
                border: '1px solid var(--gray-300)',
              }}
            >
              <Text strong style={{ color: 'var(--text-heading)', fontSize: 'var(--font-size-md)' }}>
                {t('noPermissionsView.interfaceLabel', 'Interface')}: {displayTitle}
              </Text>
              <br />
              <Text style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-base)' }}>{displayDescription}</Text>
            </div>

            <div
              style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}
            >
              <Button
                type='primary' icon={<UserOutlined />}
                size='large' onClick={handleGoToProfile}
                style={{
                  height: '44px',
                  padding: '0 24px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '15px',
                  fontWeight: '500',
                }}
              >
                {t('noPermissionsView.buttons.viewProfile', 'Voir mon Profil')}
              </Button>

              <Button
                icon={<KeyOutlined />}
                size='large' onClick={handleRequestPermissions}
                style={{
                  height: '44px',
                  padding: '0 24px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '15px',
                  fontWeight: '500',
                }}
              >
                {t('noPermissionsView.buttons.requestPermissions', 'Demander des Permissions')}
              </Button>

              <Button
                icon={<EyeOutlined />}
                size='large' onClick={handleShowPermissions}
                style={{
                  height: '44px',
                  padding: '0 24px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '15px',
                  fontWeight: '500',
                  background: 'var(--info-blue-bg)',
                  borderColor: 'var(--primary-color)',
                  color: 'var(--primary-color)',
                }}
              >
                {t('noPermissionsView.buttons.viewPermissions', 'Voir mes Permissions')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Right Side - Permissions List */}
        <Card
          style={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid var(--gray-300)',
            background: 'white',
          }}
        >
          <div style={{ padding: '24px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: '1px solid var(--gray-300)',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'var(--success-green)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleOutlined style={{ color: 'white', fontSize: 'var(--font-size-lg)' }} />
              </div>
              <div>
                <Title level={3} style={{ margin: 0, color: 'var(--text-heading)', fontSize: 'var(--font-size-xl)' }}>
                  {t('noPermissionsView.requiredPermissions.title', 'Permissions Requises')}
                </Title>
                <Text type='secondary' style={{ fontSize: 'var(--font-size-base)' }}>
                  {t('noPermissionsView.requiredPermissions.count', {
                    count: displayPermissions.length,
                  })}
                </Text>
              </div>
            </div>

            {/* User's Current Permissions */}
            {(() => {
              // Extract user's permissions to show what they have
              const userPermissionsList = [];

              // From allPermissionsList
              if (userPermissions?.allPermissionsList) {
                userPermissions.allPermissionsList.forEach(permCode => {
                  userPermissionsList.push({
                    code: permCode,
                    source: 'role',
                    isOverride: false,
                  });
                });
              }

              // From permissions object (nested structure - includes overrides)
              if (userPermissions?.permissions && typeof userPermissions.permissions === 'object') {
                Object.keys(userPermissions.permissions).forEach(resourceKey => {
                  const resource = userPermissions.permissions[resourceKey];
                  if (resource && resource.actions && typeof resource.actions === 'object') {
                    Object.keys(resource.actions).forEach(actionCode => {
                      const action = resource.actions[actionCode];
                      if (action && action.effect === 'allow') {
                        const permissionCode = `${resourceKey}.${actionCode}`;
                        // Check if already added (from allPermissionsList)
                        const existingIndex = userPermissionsList.findIndex(
                          p => p.code === permissionCode
                        );
                        if (existingIndex >= 0) {
                          // Update to mark as override if it is
                          if (action.is_override === true) {
                            userPermissionsList[existingIndex].isOverride = true;
                            userPermissionsList[existingIndex].source = 'override';
                          }
                        } else {
                          // Add new permission
                          userPermissionsList.push({
                            code: permissionCode,
                            source: action.is_override === true ? 'override' : 'direct',
                            isOverride: action.is_override === true,
                          });
                        }
                      }
                    });
                  }
                });
              }

              return userPermissionsList.length > 0 ? (
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid var(--gray-300)',
                    }}
                  >
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'var(--primary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <UserOutlined style={{ color: 'white', fontSize: 'var(--font-size-lg)' }} />
                    </div>
                    <div>
                      <Title level={4} style={{ margin: 0, color: 'var(--text-heading)', fontSize: 'var(--font-size-md)' }}>
                        {t('noPermissionsView.currentPermissions.title', 'Vos Permissions')} (
                        {userPermissionsList.length})
                      </Title>
                      <Text type='secondary' style={{ fontSize: 'var(--font-size-xs)' }}>
                        {t(
                          'noPermissionsView.currentPermissions.subtitle',
                          'Permissions actuellement actives'
                        )}
                      </Text>
                    </div>
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '24px' }}>
                    {userPermissionsList.map((perm, index) => (
                      <div
                        key={perm.code || index}
                        style={{
                          padding: '8px 12px',
                          marginBottom: '6px',
                          background: perm.isOverride ? 'var(--info-blue-bg)' : 'var(--success-green-bg)',
                          borderRadius: '6px',
                          border: `1px solid ${perm.isOverride ? 'var(--info-blue-border)' : 'var(--success-green-border)'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Text style={{ color: 'var(--text-heading)', fontWeight: '500' }}>{perm.code}</Text>
                          {perm.isOverride && (
                            <Tag color='blue' size='small' style={{ marginLeft: '8px' }}>
                              {t('noPermissionsView.override', 'Override')}
                            </Tag>
                          )}
                        </div>
                        <Tag color='green' size='small'>
                          {t('noPermissionsView.active', 'Actif')}
                        </Tag>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {displayPermissions.map((permission, index) => {
                // Check if user has this permission
                const userHasPermission = (() => {
                  const permissionCode = permission.code;

                  // Check in allPermissionsList
                  if (userPermissions?.allPermissionsList?.includes(permissionCode)) {
                    return { has: true, source: 'role' };
                  }

                  // Check in permissions object
                  if (userPermissions?.permissions) {
                    const parts = permissionCode.split('.');
                    if (parts.length >= 2) {
                      const resourceKey = parts.slice(0, -1).join('.');
                      const actionCode = parts[parts.length - 1];
                      const resource = userPermissions.permissions[resourceKey];
                      if (resource && resource.actions && resource.actions[actionCode]) {
                        const action = resource.actions[actionCode];
                        if (action.effect === 'allow') {
                          return {
                            has: true,
                            source: action.is_override === true ? 'override' : 'direct',
                            isOverride: action.is_override === true,
                          };
                        }
                      }
                    }
                  }

                  return { has: false };
                })();

                return (
                  <div
                    key={permission.code}
                    style={{
                      padding: '16px',
                      marginBottom: '8px',
                      background: userHasPermission.has ? 'var(--success-green-bg)' : 'var(--gray-200)',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${userHasPermission.has ? 'var(--success-green-border)' : 'var(--gray-300)'}`,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = userHasPermission.has
                        ? '#dcfce7'
                        : '#f1f5f9';
                      e.currentTarget.style.borderColor = userHasPermission.has
                        ? '#86efac'
                        : '#cbd5e1';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = userHasPermission.has
                        ? '#f0fdf4'
                        : '#f8fafc';
                      e.currentTarget.style.borderColor = userHasPermission.has
                        ? '#bbf7d0'
                        : '#e2e8f0';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: userHasPermission.has ? 'var(--success-green)' : 'var(--text-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        {userHasPermission.has ? (
                          <CheckCircleOutlined style={{ color: 'white', fontSize: 'var(--font-size-xs)' }} />
                        ) : (
                          <LockOutlined style={{ color: 'white', fontSize: 'var(--font-size-xs)' }} />
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Text
                            strong
                            style={{
                              color: 'var(--text-heading)',
                              fontSize: '15px',
                            }}
                          >
                            {permission.name}
                          </Text>
                          <Tag
                            color={userHasPermission.has ? 'green' : 'blue'}
                            style={{
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              border: 'none',
                            }}
                          >
                            {permission.code}
                          </Tag>
                          {userHasPermission.has && (
                            <Tag
                              color={userHasPermission.isOverride ? 'purple' : 'green'}
                              style={{
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '500',
                              }}
                            >
                              {userHasPermission.isOverride
                                ? t('noPermissionsView.hasPermission.override', 'Override ✓')
                                : t(
                                    'noPermissionsView.hasPermission.youHaveIt',
                                    'Vous avez cette permission ✓'
                                  )}
                            </Tag>
                          )}
                        </div>
                        <Text style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', lineHeight: '1.4' }}>
                          {permission.description}
                        </Text>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop: '24px',
                padding: '16px',
                background: 'var(--info-blue-bg)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--info-blue-border)',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
              >
                <InfoCircleOutlined style={{ color: 'var(--color-primary-600)', fontSize: 'var(--font-size-md)' }} />
                <Text strong style={{ color: 'var(--text-heading)', fontSize: 'var(--font-size-base)' }}>
                  {t(
                    'noPermissionsView.howToGetPermissions.title',
                    'Comment obtenir ces permissions?'
                  )}
                </Text>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', lineHeight: '1.5' }}>
                <p style={{ margin: '0 0 6px 0' }}>
                  {t(
                    'noPermissionsView.howToGetPermissions.intro',
                    'Pour accéder à cette interface, vous pouvez:'
                  )}
                </p>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  <li>
                    {t(
                      'noPermissionsView.howToGetPermissions.option1',
                      'Demander ces permissions à votre administrateur système'
                    )}
                  </li>
                  <li>
                    {t(
                      'noPermissionsView.howToGetPermissions.option2',
                      'Demander un rôle qui inclut ces permissions'
                    )}
                  </li>
                  <li>
                    {t(
                      'noPermissionsView.howToGetPermissions.option3',
                      'Utiliser le bouton "Demander des Permissions" ci-contre'
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* User Permissions Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined style={{ color: 'var(--primary-color)' }} />
            <span>{t('noPermissionsView.modal.title', 'Mes Permissions et Rôles')}</span>
          </div>
        }
        open={permissionsModalVisible}
        onCancel={() => setPermissionsModalVisible(false)}
        footer={[
          <Button key='close' onClick={() => setPermissionsModalVisible(false)}>
            {t('noPermissionsView.modal.close', 'Fermer')}
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {permissionsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size='large' />
            <div style={{ marginTop: '16px', color: 'var(--text-muted)' }}>
              {t('noPermissionsView.modal.loading', 'Chargement de vos permissions...')}
            </div>
          </div>
        ) : userPermissions ? (
          <div>
            {/* User Info */}
            <div
              style={{
                marginBottom: '24px',
                padding: '16px',
                background: 'var(--gray-200)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}
              >
                <UserOutlined style={{ color: 'var(--primary-color)' }} />
                <Text strong style={{ fontSize: 'var(--font-size-md)' }}>
                  {user.name || user.email}
                </Text>
                <Tag color='blue'>{user.role}</Tag>
              </div>
              <Text type='secondary'>ID: {user.id}</Text>
            </div>

            {/* Super Admin Check */}
            {userPermissions.__superAdmin && (
              <div
                style={{
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'var(--warning-bg)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--warning-color)',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
                >
                  <CheckCircleOutlined style={{ color: 'var(--warning-color)' }} />
                  <Text strong style={{ color: '#92400e' }}>
                    {t('noPermissionsView.modal.superAdmin.title', 'Super Administrateur')}
                  </Text>
                </div>
                <Text style={{ color: '#92400e' }}>
                  {t(
                    'noPermissionsView.modal.superAdmin.description',
                    'Vous avez accès complet à toutes les fonctionnalités du système.'
                  )}
                </Text>
              </div>
            )}

            {/* Roles */}
            {userPermissions.roles && userPermissions.roles.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Title
                  level={4}
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <KeyOutlined style={{ color: 'var(--primary-color)' }} />
                  {t('noPermissionsView.modal.roles.title', 'Mes Rôles')} (
                  {userPermissions.roles.length})
                </Title>
                <List
                  dataSource={userPermissions.roles}
                  renderItem={role => (
                    <List.Item>
                      <div style={{ width: '100%' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <div>
                            <Text strong style={{ fontSize: '15px' }}>
                              {role.name}
                            </Text>
                            <div style={{ marginTop: '4px' }}>
                              <Text type='secondary' style={{ fontSize: 'var(--font-size-sm)' }}>
                                {role.description}
                              </Text>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Tag color={role.is_active ? 'green' : 'red'}>
                              {role.is_active
                                ? t('noPermissionsView.modal.roles.active', 'Actif')
                                : t('noPermissionsView.modal.roles.inactive', 'Inactif')}
                            </Tag>
                            {role.is_immutable && (
                              <Tag color='orange'>
                                {t('noPermissionsView.modal.roles.immutable', 'Immutable')}
                              </Tag>
                            )}
                          </div>
                        </div>
                        {role.permissions && role.permissions.length > 0 && (
                          <div>
                            <Text type='secondary' style={{ fontSize: 'var(--font-size-xs)' }}>
                              {t(
                                'noPermissionsView.modal.roles.permissionsLabel',
                                'Permissions du Rôle'
                              )}{' '}
                              ({role.permissions.length}):
                            </Text>
                            <div
                              style={{
                                marginTop: '4px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                              }}
                            >
                              {role.permissions.map((permission, index) => (
                                <Tag key={index} size='small' color='green'>
                                  {permission.permission_code ||
                                    permission.permission_name ||
                                    permission}
                                </Tag>
                              ))}
                            </div>
                            {/* Show detailed permission information */}
                            <div style={{ marginTop: '8px' }}>
                              {role.permissions.map((permission, index) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: '4px',
                                    padding: '4px 8px',
                                    background: 'var(--info-blue-bg)',
                                    borderRadius: 'var(--radius-xs)',
                                    fontSize: '11px',
                                  }}
                                >
                                  <div style={{ fontWeight: 'bold', color: 'var(--color-primary-900)' }}>
                                    {permission.permission_code || permission.permission_name}
                                  </div>
                                  {permission.description && (
                                    <div style={{ color: 'var(--color-primary-700)', marginTop: '2px' }}>
                                      {permission.description}
                                    </div>
                                  )}
                                  <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>
                                    Effect: {permission.effect || 'allow'}
                                    {permission.integrated && ' • Intégré'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{ marginTop: '8px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                          {t('noPermissionsView.modal.roles.grantedAt', 'Accordé le')}:{' '}
                          {moment(role.granted_at).format('DD/MM/YYYY')}
                          {role.expires_at && (
                            <span>
                              {' '}
                              • {t('noPermissionsView.modal.roles.expiresAt', 'Expire le')}:{' '}
                              {moment(role.expires_at).format('DD/MM/YYYY')}
                            </span>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Direct Permissions - Extract from nested structure */}
            {(() => {
              // Extract all permissions from nested structure: { resource: { actions: { action: {...} } } }
              const extractedPermissions = [];

              if (userPermissions.permissions && typeof userPermissions.permissions === 'object') {
                Object.keys(userPermissions.permissions).forEach(resourceKey => {
                  const resource = userPermissions.permissions[resourceKey];
                  if (resource && resource.actions && typeof resource.actions === 'object') {
                    Object.keys(resource.actions).forEach(actionCode => {
                      const action = resource.actions[actionCode];
                      if (action && action.effect === 'allow') {
                        const permissionCode = `${resourceKey}.${actionCode}`;
                        extractedPermissions.push({
                          code: permissionCode,
                          resource: resourceKey,
                          action: actionCode,
                          effect: action.effect,
                          isOverride: action.is_override === true,
                          deleted_at: action.deleted_at,
                        });
                      }
                    });
                  }
                });
              }

              // Also check for flat structure (legacy format)
              if (extractedPermissions.length === 0 && userPermissions.permissions) {
                Object.entries(userPermissions.permissions).forEach(([permission, value]) => {
                  if (permission !== '__superAdmin' && permission !== 'success') {
                    if (typeof value === 'boolean') {
                      extractedPermissions.push({
                        code: permission,
                        effect: value ? 'allow' : 'deny',
                        isOverride: false,
                      });
                    } else if (typeof value === 'object' && value.effect) {
                      extractedPermissions.push({
                        code: permission,
                        effect: value.effect,
                        isOverride: value.is_override === true,
                      });
                    }
                  }
                });
              }

              return extractedPermissions.length > 0 ? (
                <div style={{ marginTop: '24px' }}>
                  <Title
                    level={4}
                    style={{
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <LockOutlined style={{ color: 'var(--primary-color)' }} />
                    {t('noPermissionsView.modal.directPermissions.title', 'Permissions Directes')} (
                    {extractedPermissions.length})
                  </Title>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {extractedPermissions.map((perm, index) => (
                      <div
                        key={perm.code || index}
                        style={{
                          padding: '12px',
                          background: perm.effect === 'allow' ? 'var(--success-green-bg)' : 'var(--error-bg)',
                          borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${perm.effect === 'allow' ? 'var(--success-green-border)' : 'var(--error-border)'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{ fontWeight: 'bold', color: 'var(--text-heading)', marginBottom: '4px' }}
                          >
                            {perm.code}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                            {t('noPermissionsView.modal.directPermissions.effect', 'Effect')}:{' '}
                            {perm.effect}
                            {perm.isOverride && (
                              <Tag color='blue' size='small' style={{ marginLeft: '8px' }}>
                                {t('noPermissionsView.override', 'Override')}
                              </Tag>
                            )}
                          </div>
                        </div>
                        <Tag
                          color={perm.effect === 'allow' ? 'green' : 'red'}
                          style={{ marginLeft: '12px' }}
                        >
                          {perm.effect === 'allow'
                            ? t('noPermissionsView.modal.directPermissions.allow', 'Allow')
                            : t('noPermissionsView.modal.directPermissions.deny', 'Deny')}
                        </Tag>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Partner Direct Permissions (Detailed) */}
            {userPermissions.directPermissions && userPermissions.directPermissions.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <Title
                  level={4}
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <LockOutlined style={{ color: 'var(--primary-color)' }} />
                  {t(
                    'noPermissionsView.modal.detailedPermissions.title',
                    'Permissions Détaillées'
                  )}{' '}
                  ({userPermissions.directPermissions.length})
                </Title>
                <List
                  dataSource={userPermissions.directPermissions}
                  renderItem={permission => (
                    <List.Item>
                      <div style={{ width: '100%' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <div>
                            <Text strong style={{ fontSize: '15px' }}>
                              {permission.permission_name || permission.permission_code}
                            </Text>
                            <div style={{ marginTop: '4px' }}>
                              <Text type='secondary' style={{ fontSize: 'var(--font-size-sm)' }}>
                                {permission.description ||
                                  t(
                                    'noPermissionsView.modal.detailedPermissions.noDescription',
                                    'Aucune description disponible'
                                  )}
                              </Text>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Tag color={permission.is_active ? 'green' : 'red'}>
                              {permission.is_active
                                ? t('noPermissionsView.active', 'Actif')
                                : t('noPermissionsView.modal.roles.inactive', 'Inactif')}
                            </Tag>
                          </div>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                          {t('noPermissionsView.modal.detailedPermissions.code', 'Code')}:{' '}
                          {permission.permission_code}
                          {permission.granted_at && (
                            <span>
                              {' '}
                              • {t('noPermissionsView.modal.roles.grantedAt', 'Accordé le')}:{' '}
                              {moment(permission.granted_at).format('DD/MM/YYYY')}
                            </span>
                          )}
                          {permission.expires_at && (
                            <span>
                              {' '}
                              • {t('noPermissionsView.modal.roles.expiresAt', 'Expire le')}:{' '}
                              {moment(permission.expires_at).format('DD/MM/YYYY')}
                            </span>
                          )}
                          {permission.granted_by_user && (
                            <span>
                              {' '}
                              •{' '}
                              {t(
                                'noPermissionsView.modal.detailedPermissions.grantedBy',
                                'Accordé par'
                              )}
                              : {permission.granted_by_user}
                            </span>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Error message */}
            {userPermissions.error && (
              <div
                style={{
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'var(--error-bg)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid #fca5a5',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
                >
                  <InfoCircleOutlined style={{ color: '#dc2626' }} />
                  <Text strong style={{ color: '#dc2626' }}>
                    {t('noPermissionsView.modal.error.title', "Limitation d'accès")}
                  </Text>
                </div>
                <Text style={{ color: '#dc2626', fontSize: 'var(--font-size-base)' }}>
                  {userPermissions.error.details}
                </Text>
                <div style={{ marginTop: '8px', fontSize: 'var(--font-size-xs)', color: '#991b1b' }}>
                  {t('noPermissionsView.modal.error.label', 'Erreur')}:{' '}
                  {userPermissions.error.message} (
                  {t('noPermissionsView.modal.error.status', 'Status')}:{' '}
                  {userPermissions.error.status})
                </div>
              </div>
            )}

            {/* No permissions message */}
            {(!userPermissions.roles || userPermissions.roles.length === 0) &&
              (!userPermissions.permissions ||
                Object.keys(userPermissions.permissions).length === 0) &&
              !userPermissions.__superAdmin &&
              !userPermissions.error && (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <InfoCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>
                    {t(
                      'noPermissionsView.modal.noPermissions.title',
                      'Aucune permission ou rôle assigné'
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-base)', marginTop: '8px' }}>
                    {t(
                      'noPermissionsView.modal.noPermissions.message',
                      'Contactez votre administrateur pour obtenir des permissions.'
                    )}
                  </div>
                </div>
              )}

            {/* Basic user info when API fails */}
            {userPermissions.error && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: 'var(--info-blue-bg)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--primary-color)',
                }}
              >
                <Title level={5} style={{ marginBottom: '12px', color: 'var(--color-primary-900)' }}>
                  {t('noPermissionsView.modal.basicInfo.title', 'Informations de base')}
                </Title>
                <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-primary-900)' }}>
                  <div>
                    <strong>{t('noPermissionsView.modal.basicInfo.name', 'Nom')}:</strong>{' '}
                    {userPermissions.userInfo.name ||
                      t('noPermissionsView.modal.basicInfo.notDefined', 'Non défini')}
                  </div>
                  <div>
                    <strong>{t('noPermissionsView.modal.basicInfo.email', 'Email')}:</strong>{' '}
                    {userPermissions.userInfo.email ||
                      t('noPermissionsView.modal.basicInfo.notDefined', 'Non défini')}
                  </div>
                  <div>
                    <strong>{t('noPermissionsView.modal.basicInfo.role', 'Rôle')}:</strong>{' '}
                    {userPermissions.userInfo.role ||
                      t('noPermissionsView.modal.basicInfo.notDefined', 'Non défini')}
                  </div>
                  <div>
                    <strong>{t('noPermissionsView.modal.basicInfo.id', 'ID')}:</strong>{' '}
                    {userPermissions.userInfo.id ||
                      t('noPermissionsView.modal.basicInfo.notDefined', 'Non défini')}
                  </div>
                </div>
                <div style={{ marginTop: '12px', fontSize: 'var(--font-size-xs)', color: 'var(--color-primary-700)' }}>
                  <strong>{t('noPermissionsView.modal.basicInfo.note', 'Note')}:</strong>{' '}
                  {t(
                    'noPermissionsView.modal.basicInfo.noteMessage',
                    'Pour voir vos permissions détaillées, contactez votre administrateur système.'
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error-color)' }}>
            <InfoCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <div>
              {t(
                'noPermissionsView.modal.loadingError.title',
                'Erreur lors du chargement des permissions'
              )}
            </div>
            <div style={{ fontSize: 'var(--font-size-base)', marginTop: '8px' }}>
              {t(
                'noPermissionsView.modal.loadingError.message',
                'Impossible de récupérer vos permissions. Veuillez réessayer.'
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NoPermissionsView;
